// src/pages/RoleManagement.js
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';
import { confirmDialog, alertDialog } from '../utils/dialog';
import Table from '../components/Table';
import RoleForm from '../components/RoleForm';
import {
  PlusIcon, EditIcon, TrashIcon, SearchIcon, ShieldIcon, AlertTriangleIcon,
} from '../components/Icon';

export default function RoleManagement() {
  const user = getCurrentUser();
  const canCreate = can(user, 'roles.create');
  const canEdit   = can(user, 'roles.edit');
  const canDelete = can(user, 'roles.delete');

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [search, setSearch]   = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | role object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.roles();
      setRows(res.roles || []);
      setError(false);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (r) => {
    const ok = await confirmDialog({
      title: 'Delete Role',
      message: `Delete role "${r.label || r.name}"?\nThis cannot be undone.`,
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    const res = await api.roleDelete(r.id);
    if (res?.success) load();
    else alertDialog({ title: 'Delete Failed', message: res?.error || 'Delete failed', tone: 'danger' });
  };

  const handleSaved = () => { setEditing(null); load(); };

  const filtered = rows.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.name?.toLowerCase().includes(s) || r.label?.toLowerCase().includes(s);
  });

  const isSystem = (r) => r.is_system === 1 || r.is_system === '1';

  const columns = [
    { key: 'label', header: 'Role', nowrap: true, render: (r) => (
      <div className="proj-code">
        {r.label || r.name}
        <span className="proj-prefix" title="Role id used internally">{r.name}</span>
      </div>
    ) },
    { key: 'permissions', header: 'Permissions', render: (r) => (
      r.name === 'admin'
        ? <span className="pill pill--role">Full access</span>
        : <span style={{ fontSize: 12, color: '#555' }}>{(r.permissions || []).length} permission{(r.permissions || []).length === 1 ? '' : 's'}</span>
    ) },
    { key: 'type', header: 'Type', align: 'center', render: (r) => (
      <span className={`pill pill--${isSystem(r) ? 'role' : 'default'}`}>{isSystem(r) ? 'System' : 'Custom'}</span>
    ) },
    { key: 'actions', header: 'Actions', nowrap: true, align: 'right', render: (r) => (
      <>
        <button
          className="icon-btn icon-btn--edit"
          onClick={() => setEditing(r)}
          disabled={!canEdit}
          title={canEdit ? 'Edit' : 'No permission'}
          aria-label="Edit"
        ><EditIcon size={13} /></button>
        {canDelete && !isSystem(r) && (
          <button className="icon-btn icon-btn--del" onClick={() => handleDelete(r)} title="Delete" aria-label="Delete">
            <TrashIcon size={13} color="#c0392b" />
          </button>
        )}
      </>
    ) },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Roles &amp; Access</h1>
          <div className="page-sub">{rows.length} role{rows.length === 1 ? '' : 's'} · controls sidebar &amp; button access</div>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={() => setEditing('new')}>
            <PlusIcon size={14} /> <span>Add Role</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="toolbar">
        <div className="search-wrap">
          <SearchIcon size={14} className="search-icon" />
          <input
            className="search-input"
            placeholder="Search by role name or label..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {search && <button className="reset-btn" onClick={() => setSearch('')}>Reset</button>}
      </div>

      {/* Content */}
      <div className="table-card">
        {loading ? (
          <div className="loader-wrap"><div className="spinner" /></div>
        ) : error ? (
          <div className="empty-state">
            <div><AlertTriangleIcon size={42} color="#c0392b" /></div>
            <div className="um-err-title">Cannot load roles</div>
            <div className="um-err-sub">Check the API connection (and that the roles endpoint is deployed).</div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={filtered}
            rowKey={(r) => r.id}
            minWidth={720}
            maxHeight="62vh"
            emptyMessage={
              <div>
                <div className="empty-icon-muted"><ShieldIcon size={40} /></div>
                <div className="empty-sub">No roles found</div>
              </div>
            }
          />
        )}
      </div>

      {editing && (
        <RoleForm
          role={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
